import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

// Compact searchable dropdown for large option lists (faculty, subjects).
export const SearchSelect = ({ value, onChange, options, placeholder = 'Select', testId, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = options.find(o => o.value === value);
  const ql = q.trim().toLowerCase();
  const filtered = !ql ? options : options.filter(o =>
    (o.label || '').toLowerCase().includes(ql) || (o.sub || '').toLowerCase().includes(ql));

  const h = size === 'sm' ? 'h-7 text-[11px]' : 'h-9 text-sm';

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => { setOpen(o => !o); setQ(''); }}
        className={`w-full ${h} border rounded px-1.5 flex items-center justify-between gap-1 bg-white ${selected ? 'border-slate-300 text-slate-800' : 'border-slate-200 text-slate-400'}`}>
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown className="w-3 h-3 shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 max-w-[240px] bg-white border border-slate-200 rounded-lg shadow-xl">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100">
            <Search className="w-3 h-3 text-slate-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search..."
              data-testid={testId ? `${testId}-search` : undefined}
              className="flex-1 text-xs outline-none bg-transparent" />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              className="w-full text-left px-2 py-1 text-[11px] text-slate-400 hover:bg-slate-50 flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
            {filtered.length === 0 && <div className="px-2 py-2 text-[11px] text-slate-400">No matches</div>}
            {filtered.slice(0, 200).map(o => (
              <button type="button" key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left px-2 py-1 text-[11px] hover:bg-violet-50 ${o.value === value ? 'bg-violet-50 font-semibold' : ''}`}>
                <span className="text-slate-800">{o.label}</span>
                {o.sub && <span className="text-slate-400 ml-1">{o.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-select variant (checkboxes, stays open on pick) — used where a session can have
// several faculty at once, e.g. a practical run jointly by 3-4 of them.
export const MultiSearchSelect = ({ values = [], onChange, options, placeholder = 'Select', testId, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedOptions = options.filter(o => values.includes(o.value));
  const ql = q.trim().toLowerCase();
  const filtered = !ql ? options : options.filter(o =>
    (o.label || '').toLowerCase().includes(ql) || (o.sub || '').toLowerCase().includes(ql));

  const toggle = (v) => { onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]); };

  const h = size === 'sm' ? 'min-h-[1.75rem] text-[11px]' : 'min-h-9 text-sm';

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => { setOpen(o => !o); setQ(''); }}
        className={`w-full ${h} border rounded px-1.5 py-0.5 flex items-center justify-between gap-1 bg-white ${selectedOptions.length ? 'border-slate-300' : 'border-slate-200'}`}>
        <span className="truncate flex-1 text-left">
          {selectedOptions.length === 0
            ? <span className="text-slate-400">{placeholder}</span>
            : <span className="text-slate-800">{selectedOptions.map(o => o.label).join(', ')}</span>}
        </span>
        <ChevronDown className="w-3 h-3 shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 max-w-[240px] bg-white border border-slate-200 rounded-lg shadow-xl">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100">
            <Search className="w-3 h-3 text-slate-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search..."
              data-testid={testId ? `${testId}-search` : undefined}
              className="flex-1 text-xs outline-none bg-transparent" />
          </div>
          {selectedOptions.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              className="w-full text-left px-2 py-1 text-[11px] text-slate-400 hover:bg-slate-50 flex items-center gap-1 border-b border-slate-100">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && <div className="px-2 py-2 text-[11px] text-slate-400">No matches</div>}
            {filtered.slice(0, 200).map(o => {
              const checked = values.includes(o.value);
              return (
                <button type="button" key={o.value} onClick={() => toggle(o.value)}
                  className={`w-full text-left px-2 py-1 text-[11px] hover:bg-violet-50 flex items-center gap-1.5 ${checked ? 'bg-violet-50 font-semibold' : ''}`}>
                  <span className={`w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center ${checked ? 'bg-violet-600 border-violet-600' : 'border-slate-300'}`}>
                    {checked && <Check className="w-2.5 h-2.5 text-white" />}
                  </span>
                  <span className="text-slate-800 truncate">{o.label}</span>
                  {o.sub && <span className="text-slate-400 ml-1 truncate">{o.sub}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
